import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CommunicationsQueryKeys from './CommunicationsQueryKeys';
const communicationQueryKeys = new CommunicationsQueryKeys();
export const useInvalidateCommunicationsCache = () => {
    const queryClient = useQueryClient();
    const invalidateCommunicationsCache = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: communicationQueryKeys.getCommunications(),
        });
    }, [queryClient]);
    return invalidateCommunicationsCache;
};
export const useOptimisticCommunicationDocumentsCopyCacheUpdate = () => {
    const queryClient = useQueryClient();
    const onMutate = useCallback(async ({ communicationId, documentIds }) => {
        const queryKey = communicationQueryKeys.getCommunication(communicationId);
        await queryClient.cancelQueries({ queryKey });
        const previousCommunicationData = queryClient.getQueryData(queryKey);
        if (!previousCommunicationData) {
            return {};
        }
        const nextData = { ...previousCommunicationData, documents: previousCommunicationData.documents.map((doc) => {
                if (documentIds.includes(doc.documentId)) {
                    return {
                        ...doc,
                        isAvailable: false,
                    };
                }
                return doc;
            }) };
        queryClient.setQueryData(queryKey, nextData);
        return { previousCommunicationData };
    }, [queryClient]);
    const onError = useCallback(async (_, { communicationId }, context) => {
        if (context?.previousCommunicationData) {
            const queryKey = communicationQueryKeys.getCommunication(communicationId);
            queryClient.setQueryData(queryKey, context.previousCommunicationData);
        }
    }, [queryClient]);
    return {
        onMutate,
        onError,
    };
};
export const useOptimisticCommunicationsDocumentsCopyCacheUpdate = () => {
    const queryClient = useQueryClient();
    const onMutate = useCallback(async ({ communicationId, documentIds }) => {
        const queryKey = communicationQueryKeys.getCommunications();
        await queryClient.cancelQueries({ queryKey });
        const previousCommunicationsData = queryClient.getQueryData(queryKey);
        if (!previousCommunicationsData) {
            return {};
        }
        const updatedData = {
            ...previousCommunicationsData,
            pages: previousCommunicationsData.pages.map(page => ({
                ...page,
                items: page.communications.map((item) => {
                    if (item.id === communicationId) {
                        return {
                            ...item,
                            documents: item.documents.map((document) => {
                                if (documentIds.includes(document.documentId)) {
                                    return { ...document, isAvailable: false };
                                }
                                return document;
                            }),
                        };
                    }
                    return item;
                }),
            })),
        };
        queryClient.setQueryData(queryKey, updatedData);
        return { previousCommunicationsData };
    }, [queryClient]);
    const onError = useCallback(async (_, __, context) => {
        if (context?.previousCommunicationsData) {
            const queryKey = communicationQueryKeys.getCommunications();
            queryClient.setQueryData(queryKey, context.previousCommunicationsData);
        }
    }, [queryClient]);
    return {
        onMutate,
        onError,
    };
};
